import { useTranslation } from 'react-i18next'
import React, { useState } from 'react'
import { RequestProps } from '../../store/types'
import defaultProps from '../../config/defaultProps'
import { EmployeeFullName, useGetRolesDetailedQuery } from '../../store/redux/reducers/rolesSlice'
import { StyledPageContent } from '../../styles/PageStyles'
import { Box, Button, Collapse, Stack, TableCell, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'
import { FilterList } from '@mui/icons-material'
import DataTable from './DataTable'
import { Column, Row } from '../../store/types/utils/DataTableUtils'
import FilterRolesMenu from '../FilterMenu/Roles/FilterRolesMenu'
import { PermissionLight } from '../../store/redux/reducers/permissionsSlice'
import LocalStorageTypes from '../../store/types/enums/LocalStorageTypes'
import SessionStorageKeys from '../../store/types/enums/SessionStorageKeys'
import usePersistedPage from '../../store/utils/usePersistedPage'

const RoleDetailTable = () => {
  const { t } = useTranslation()
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false)
  const [page, setPage] = usePersistedPage(SessionStorageKeys.RolesTablePage)
  const [requestProps, setRequestProps] = useState<RequestProps>(defaultProps)
  const theme = useTheme()

  const { data: rolesResponse, isLoading } = useGetRolesDetailedQuery(requestProps)

  const isDownMd = useMediaQuery(theme.breakpoints.down('md'))
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))

  const convertPermissionsToText = (permissions: PermissionLight[]) => {
    return permissions && permissions.length > 0
      ? permissions.map((permission: { id: string }) => `${permission.id}`).join(', ')
      : '-'
  }

  const convertEmployeesToText = (employees: EmployeeFullName[]) => {
    return employees && employees.length > 0
      ? employees
          .map((employee: { firstName: string; lastName: string }) => `${employee.firstName} ${employee.lastName}`)
          .join(', ')
      : '-'
  }

  const columns: Column[] = [
    { id: 'name', label: t('components.table.header.roleName') },
    { id: 'permissions.id', label: t('components.table.header.permissions') },
    { id: 'employees.firstName', label: t('components.table.header.employees') },
  ]

  const renderRoleTile = (role: Row, labelId: string, checkbox?: React.ReactNode) => {
    return (
      <Stack
        key={labelId}
        direction="column"
        justifyContent="space-between"
        sx={{
          minWidth: 100,
          maxWidth: 500,
          overflow: 'hidden',
        }}
      >
        {checkbox && checkbox}
        <Typography variant="h6" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {t(`roles.${role.name}`)}
        </Typography>
        {role.permissions.length > 0 && (
          <Tooltip title={convertPermissionsToText(role.permissions)} placement={'bottom-start'}>
            <Box
              marginTop={role.permissions.length > 0 || role.employees.length > 0 ? '8px' : ''}
              sx={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
              }}
            >
              <Typography>{convertPermissionsToText(role.permissions)}</Typography>
            </Box>
          </Tooltip>
        )}
        {role.employees.length > 0 && (
          <Box
            sx={{
              width: '100%',
              maxWidth: 500,
              minWidth: 100,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            marginTop="8px"
            marginBottom="0 !important"
          >
            <Tooltip title={convertEmployeesToText(role.employees)} placement={'bottom-start'}>
              <Typography
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%',
                }}
              >
                {convertEmployeesToText(role.employees)}
              </Typography>
            </Tooltip>
          </Box>
        )}
      </Stack>
    )
  }

  const renderRow = (role: Row) => {
    return (
      <>
        <TableCell sx={{ height: 52 }}>
          <Typography>{t(`roles.${role.name}`)}</Typography>
        </TableCell>
        <TableCell
          sx={{
            height: 52,
            width: { lgUp: 500, lgDown: 300, mdDown: 100 },
            maxWidth: 500,
            minWidth: 100,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          <Tooltip title={convertPermissionsToText(role.permissions)} placement={'bottom-start'}>
            <Typography
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {convertPermissionsToText(role.permissions)}
            </Typography>
          </Tooltip>
        </TableCell>
        <TableCell
          sx={{
            height: 52,
            width: { lgUp: 500, lgDown: 300, mdDown: 100 },
            maxWidth: 500,
            minWidth: 100,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          <Tooltip title={convertEmployeesToText(role.employees)} placement={'bottom-start'}>
            <Typography
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {convertEmployeesToText(role.employees)}
            </Typography>
          </Tooltip>
        </TableCell>
      </>
    )
  }

  return (
    <StyledPageContent display={'flex'} flexDirection={'column'} gap={'24px'} color={'text.light'}>
      <Box>
        <Button
          variant={'contained'}
          color={'secondary'}
          disableElevation
          startIcon={<FilterList />}
          size={'medium'}
          className={isFilterMenuOpen ? 'selected' : ''}
          onClick={() => {
            setIsFilterMenuOpen(!isFilterMenuOpen)
          }}
          fullWidth={isDownSm}
        >
          {t('page.users.buttons.filter')}
        </Button>
      </Box>
      <Box
        display={'flex'}
        flexDirection={{ xs: 'column', md: 'row' }}
        justifyContent={'space-between'}
        gap={isFilterMenuOpen ? 2 : 0}
        height={'fit-content'}
      >
        {isDownMd && (
          <Collapse in={isFilterMenuOpen} orientation={'vertical'}>
            <FilterRolesMenu requestProps={requestProps} setRequestProps={setRequestProps} setPage={setPage} />
          </Collapse>
        )}
        {!isDownMd && (
          <Collapse in={isFilterMenuOpen} orientation={'horizontal'}>
            <FilterRolesMenu requestProps={requestProps} setRequestProps={setRequestProps} setPage={setPage} />
          </Collapse>
        )}
        <DataTable
          renderTileContent={(role: Row, labelId: string, checkbox?: React.ReactNode) =>
            renderRoleTile(role, labelId, checkbox)
          }
          singleSelection={false}
          columns={columns}
          data={rolesResponse?.content || []}
          renderRow={renderRow}
          totalElements={rolesResponse?.totalElements || 0}
          setRequestProps={setRequestProps}
          translationForNotFound={t('page.adminsPage.accessibility.rolesNotFound')}
          page={page}
          setPage={setPage}
          isLoading={isLoading}
          removeCheckboxes
          hoverOff
          isLastCellNormal={true}
          commonRowsPerPage={
            localStorage.getItem(LocalStorageTypes.RolesRows)
              ? parseInt(localStorage.getItem(LocalStorageTypes.RolesRows)!)
              : undefined
          }
          localStorageRowsName={LocalStorageTypes.RolesRows}
        />
      </Box>
    </StyledPageContent>
  )
}

export default RoleDetailTable
